import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { creditCardSchema } from '../../validation/paymentValidation'
import SfInput from '../../../../../components/overrides/SfInput'
import BillingAddress from './BillingAddress'
import NxTextInput from 'components/inputs/NxTextInput'
import { usePaymentStore } from 'stores/paymentStore'
import SfCheckbox from 'components/overrides/SfCheckbox'
import Formatters from 'utils/formatters'

interface FormValues {
  nameOnCard: string
  cardNumber: string
  expirationMonth: string
  expirationYear: string
  CVV: string
}

export interface BillingAddressFormValues {
  firstName: string
  lastName: string
  street: string
  street2: string
  city: string
  state: string
  zipCode: string
}

export const formatCardNumber = ( cardNumber: string ) => {
  const cleanCardNumber = cardNumber.replace( /\D/g, `` )

  return cleanCardNumber.match( /.{1,4}/g )?.join( `-` ) || ``
}

const AddCreditCard: React.FC = () => {
  const [ showErrors, setShowErrors ] = useState<boolean>( false )

  const {
    newCardDetails,
    setNewCardDetails,
    setIsNewCardValid,
    savePaymentProfile,
    setSavePaymentProfile,
    splitPayment
  } = usePaymentStore()

  const { register, formState, setValue, setFocus } = useForm<FormValues>({
    resolver: zodResolver( creditCardSchema ),
    shouldFocusError: true,
    mode: `onChange`
  })

  useEffect( () => {
    setNewCardDetails({
      nameOnCard: ``,
      cardNumber: ``,
      expirationMonth: ``,
      expirationYear: ``,
      CVV: ``
    })
    setIsNewCardValid( false )
  }, [] )

  const { errors } = formState

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    numberInput = false
  ) => {
    setShowErrors( false )

    const updatedCard = {
      ...newCardDetails,
      [e.target.name]: numberInput
        ? Formatters.numericCharacters( e.target.value )
        : e.target.value
    }

    setNewCardDetails( updatedCard )

    const valid = creditCardSchema.safeParse( updatedCard ).success

    setIsNewCardValid( valid )
  }

  const onBlur = () => {
    setShowErrors( true )
  }

  return (
    <div className="flex flex-col gap-4 pl-10 pr-4 mt-3 pb-8">
      <NxTextInput
        label="Name on Card"
        required
        data-testid="card_name"
        type="text"
        id="nameOnCard"
        name="nameOnCard"
        onChange={onChange}
        onBlur={onBlur}
        register={register}
        invalid={showErrors && Boolean( errors?.nameOnCard?.message )}
        error={showErrors ? errors?.nameOnCard?.message : ``}
      />
      <NxTextInput
        label="Card Number"
        required
        data-testid="card_number"
        type="text"
        id="cardNumber"
        name="cardNumber"
        onBlur={onBlur}
        register={register}
        maxLength={20}
        onChange={( e: React.ChangeEvent<HTMLInputElement> ) => {
          setShowErrors( false )

          const formattedCardNumber = formatCardNumber( e.target.value )
          setValue( `cardNumber`, formattedCardNumber )
          const updatedCard = {
            ...newCardDetails,
            cardNumber: formattedCardNumber
          }
          setNewCardDetails( updatedCard )

          const valid = creditCardSchema.safeParse( updatedCard ).success

          setIsNewCardValid( valid )
        }}
        invalid={showErrors && Boolean( errors?.cardNumber?.message )}
        error={showErrors ? errors?.cardNumber?.message : ``}
      />
      <div className="flex gap-4 w-full">
        <div className="flex flex-col gap-2 w-full">
          <div className="w-full font-light flex">
            <p className="font-light">{`Expiration Date`}</p>
            <span className="text-error ml-1">{`*`}</span>
          </div>
          <div className="flex gap-2 items-center w-full">
            <div className="min-w-[50px] md:w-full">
              <SfInput
                required
                type="text"
                pattern="[0-9]*"
                data-testid="card_expiration_month"
                {...register( `expirationMonth`, {
                  onChange: ( e: React.ChangeEvent<HTMLInputElement> ) => {
                    setShowErrors( false )
                    const { value } = e.target
                    const formattedValue = Formatters.numericCharacters( value )
                    setValue( `expirationMonth`, formattedValue )
                    const updatedCard = {
                      ...newCardDetails,
                      expirationMonth: formattedValue
                    }
                    setNewCardDetails( updatedCard )
                    if ( formattedValue?.length === 2 ) {
                      setFocus( `expirationYear` )
                    }

                    const valid = creditCardSchema.safeParse( updatedCard ).success

                    setIsNewCardValid( valid )
                  },
                  onBlur
                })}
                maxLength={2}
                value={newCardDetails.expirationMonth}
                invalid={
                  showErrors && Boolean( errors?.expirationMonth?.message )
                }
              />
            </div>
            {`/`}
            <div className="min-w-[68px] w-full">
              <SfInput
                required
                type="text"
                pattern="[0-9]*"
                data-testid="card_expiration_year"
                {...register( `expirationYear`, {
                  onBlur,
                  onChange: ( e ) => onChange( e, true )
                })}
                maxLength={4}
                value={newCardDetails.expirationYear}
                invalid={
                  showErrors && Boolean( errors?.expirationYear?.message )
                }
              />
            </div>
          </div>
          {errors?.expirationMonth?.message && showErrors && (
            <p className="mt-2 text-error text-center mx-auto">
              {errors?.expirationMonth?.message}
            </p>
          )}
          {errors?.expirationYear?.message && showErrors && (
            <p className="mt-2 text-error text-center mx-auto">
              {errors?.expirationYear?.message}
            </p>
          )}
        </div>
        <NxTextInput
          label="CVV"
          required
          data-testid="card_securitycode"
          id="CVV"
          name="CVV"
          type="text"
          pattern="[0-9]*"
          register={register}
          onChange={( e ) => {
            setShowErrors( false )
            const formattedValue = Formatters.numericCharacters( e.target.value )
            setValue( `CVV`, formattedValue )
            const updatedCard = {
              ...newCardDetails,
              CVV: formattedValue
            }
            setNewCardDetails( updatedCard )

            const valid = creditCardSchema.safeParse( updatedCard ).success

            setIsNewCardValid( valid )
          }}
          onBlur={onBlur}
          value={newCardDetails.CVV}
          maxLength={4}
          invalid={showErrors && Boolean( errors?.CVV?.message )}
          error={showErrors ? errors?.CVV?.message : ``}
        />
      </div>

      {!splitPayment && (
        <div
          className="flex items-center justify-start gap-3 cursor-pointer"
          onClick={() => {
            setSavePaymentProfile( !savePaymentProfile )
          }}
          data-testid="snl-card-save-payment"
        >
          <SfCheckbox
            id="save-payment-profile"
            checked={savePaymentProfile}
            className="mt-1 min-w-[25px] h-[25px]"
          />
          <p className="font-light">{`Save Payment Details`}</p>
        </div>
      )}

      <BillingAddress />
    </div>
  )
}

export default AddCreditCard